import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import {
  buildCalendarGrid,
  buildSnoozeUntilIso,
  defaultSnoozeDate,
  isBeforeDay,
  isSameDay,
  SNOOZE_DEFAULT_TIME,
  startOfDay,
} from "@/lib/snooze/snoozeDateTime";
import { cn } from "@/lib/cn";

const WEEKDAYS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"] as const;

type Props = {
  open: boolean;
  title: string;
  description?: string;
  subject?: string;
  confirmLabel: string;
  previewPrefix: string;
  busy?: boolean;
  onConfirm: (untilIso: string) => void;
  onClose: () => void;
  /** Sans fond assombri — empilé sur le tiroir de rédaction. */
  overlayBackdrop?: "default" | "transparent";
};

export function ScheduleDateTimeModal({
  open,
  title,
  description,
  subject,
  confirmLabel,
  previewPrefix,
  busy = false,
  onConfirm,
  onClose,
  overlayBackdrop = "default",
}: Props) {
  const titleId = useMemo(
    () => `mail-schedule-title-${title.replace(/\s+/g, "-").toLowerCase()}`,
    [title]
  );
  const today = useMemo(() => startOfDay(new Date()), [open]);

  const [viewMonth, setViewMonth] = useState(() => defaultSnoozeDate());
  const [selectedDate, setSelectedDate] = useState(() => defaultSnoozeDate());
  const [time, setTime] = useState(SNOOZE_DEFAULT_TIME);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const initial = defaultSnoozeDate();
    setViewMonth(new Date(initial.getFullYear(), initial.getMonth(), 1));
    setSelectedDate(initial);
    setTime(SNOOZE_DEFAULT_TIME);
    setError(null);
  }, [open]);

  const monthLabel = viewMonth.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  const weeks = useMemo(() => buildCalendarGrid(viewMonth), [viewMonth]);

  const previewUntil = buildSnoozeUntilIso(selectedDate, time);

  function shiftMonth(delta: number) {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  }

  function handleConfirm() {
    const until = buildSnoozeUntilIso(selectedDate, time);
    if (!until) {
      setError("Choisissez une date et une heure dans le futur.");
      return;
    }
    onConfirm(until);
  }

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      variant="center"
      backdrop={overlayBackdrop}
      panelClassName="mail-snooze-dialog max-w-sm"
    >
      <div className="flex flex-col">
        <div className="mail-snooze-dialog-header">
          <div className="mail-snooze-dialog-icon" aria-hidden>
            <Clock className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-[var(--mail-text)]">
              {title}
            </h2>
            {subject ? (
              <p className="mt-0.5 truncate text-[11px] text-[var(--mail-muted)]" title={subject}>
                {subject}
              </p>
            ) : description ? (
              <p className="mt-0.5 text-[11px] text-[var(--mail-muted)]">{description}</p>
            ) : null}
          </div>
        </div>

        <div className="px-4 py-3">
          <div className="mail-snooze-calendar">
            <div className="mail-snooze-calendar-nav">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                className="mail-snooze-calendar-nav-btn"
                aria-label="Mois précédent"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <p className="mail-snooze-calendar-month capitalize">{monthLabel}</p>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                className="mail-snooze-calendar-nav-btn"
                aria-label="Mois suivant"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="mail-snooze-calendar-weekdays">
              {WEEKDAYS.map((label) => (
                <span key={label} className="mail-snooze-calendar-weekday">
                  {label}
                </span>
              ))}
            </div>

            <div className="mail-snooze-calendar-grid">
              {weeks.map((week, wi) =>
                week.map((day, di) => {
                  if (day == null) {
                    return <span key={`${wi}-${di}`} className="mail-snooze-calendar-pad" />;
                  }
                  const disabled = isBeforeDay(day, today);
                  const selected = isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, today);

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        setSelectedDate(day);
                        setError(null);
                      }}
                      className={cn(
                        "mail-snooze-calendar-day",
                        selected && "mail-snooze-calendar-day-selected",
                        isToday && !selected && "mail-snooze-calendar-day-today",
                        disabled && "mail-snooze-calendar-day-disabled"
                      )}
                    >
                      {day.getDate()}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="mail-snooze-time-row">
            <label htmlFor="mail-schedule-time" className="mail-snooze-time-label">
              Heure
            </label>
            <input
              id="mail-schedule-time"
              type="time"
              value={time}
              onChange={(e) => {
                setTime(e.target.value);
                setError(null);
              }}
              className="mail-snooze-time-input"
            />
          </div>

          {error ? (
            <p className="mt-2 text-xs text-[var(--mail-danger-text)]" role="alert">
              {error}
            </p>
          ) : previewUntil ? (
            <p className="mt-2 text-[11px] text-[var(--mail-muted)]">
              {previewPrefix}{" "}
              {new Date(previewUntil).toLocaleString("fr-FR", {
                weekday: "long",
                day: "numeric",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          ) : null}
        </div>

        <div className="mail-snooze-dialog-footer">
          <button type="button" className="mail-snooze-dialog-cancel" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="mail-btn-primary rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
            disabled={busy}
            onClick={handleConfirm}
          >
            {busy ? "Patientez…" : confirmLabel}
          </button>
        </div>
      </div>
    </MailModalOverlay>
  );
}
